// Dependencies
import * as React from 'react';
import { useState, useEffect } from 'react';
import {
  CreditCard, PaymentForm, GooglePay, ApplePay, CashAppPay, Afterpay, Divider,
} from 'react-square-web-payments-sdk';
import { apiService } from '@/services/api';
import { describeSquareFailure } from '@/lib/utils';
import { CARD_ON_FILE_ENABLED, EXTRA_PAY_METHODS_ENABLED, COUPONS_ENABLED } from './checkoutUi';
import CouponField from './CouponField';
import { isQuoteStaleError } from '@/lib/followupQuote';

// Vercel-env Square client config — the synchronous fallback so the card field
// mounts instantly and a slow/failed BE fetch can never block checkout.
const ENV_APP_ID = process.env.REACT_APP_SQUARE_APPLICATION_ID;
const ENV_LOCATION_ID = process.env.REACT_APP_SQUARE_LOCATION_ID || 'NY';

const fmtUsd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

// §branded-refill: `quoteId` / `visitType` / `pharmacySelection` bind the charge to
// a GET /followup/quote; `onQuoteStale(errData)` is called (instead of an alert)
// on 409 quote_stale — nothing was charged, the parent re-quotes and the patient
// confirms again. The charge is NEVER retried with the old quoteId.
const SquarePaymentForm = ({ planId, onComplete, answers, setLoading, clearSavedProgress, primaryGoal, currentStep, timestamp, email, selectedMedIds, prescriptionFulfillment, amount, phoneVerifyToken, consultFeeVisitType, cardOnFile: cardOnFileProp = false, quoteId, visitType, pharmacySelection, onQuoteStale, otoCardOnFile = false, dexaAddOn = null, nonDiscountableCents = 0 }) => {
  // §card-on-file: a flow that ALWAYS needs the card stored (a membership
  // charged by Square Subscriptions, e.g. Advanced Peptide) opts in per
  // checkout; the global flags keep governing every other checkout.
  const cardOnFile = CARD_ON_FILE_ENABLED || cardOnFileProp;
  // §square-config: prefer the BE-sourced Square ids (same merchant the BE
  // charges → FE tokenize + BE capture always match, killing the app-id/
  // merchant-mismatch bug class). Seed from env so the mount is unchanged; only
  // upgrade if the BE returns ids that actually differ (identical → no remount).
  const [sq, setSq] = useState({ applicationId: ENV_APP_ID, locationId: ENV_LOCATION_ID });
  // §coupons: the validated coupon (or null). Display-only here — the BE re-applies
  // it on capture from `couponCode`. discountedTotalCents drives the wallet amount.
  const [coupon, setCoupon] = useState(null);
  const baseCents = Number(amount) || 0;
  // Coupons never discount the DEXA add-on (owner decision; BE adds DEXA after
  // coupons). validate-coupon prices only the treatment cart, so the add-on is
  // added back on top of its discounted total.
  const extraCents = Number(nonDiscountableCents) || 0;
  const effectiveCents = coupon && Number.isFinite(coupon.discountedTotalCents)
    ? coupon.discountedTotalCents + extraCents
    : baseCents;
  // §square-config: /consult-fee keys off a real **visitType**, not the flow's
  // primary-goal answer. Several flows have no `primary-goal` slide at all
  // (branded weightloss, WSH) so primaryGoal is `undefined` there → the request
  // went out with no visitType → 400 → null → the guard silently did nothing.
  // `consultFeeVisitType` (CatalogCheckoutStep passes its catalogVisitType) is
  // the authoritative key; primaryGoal stays the fallback for legacy callers.
  const feeVisitType = consultFeeVisitType || primaryGoal;
  useEffect(() => {
    let alive = true;
    apiService.getCheckoutConfig(feeVisitType).then((cfg) => {
      const s = cfg && cfg.square;
      if (!alive || !s || !s.applicationId || !s.locationId) return;
      setSq((prev) =>
        prev.applicationId === s.applicationId && prev.locationId === s.locationId
          ? prev
          : { applicationId: s.applicationId, locationId: s.locationId },
      );
    });
    return () => { alive = false; };
  }, [feeVisitType]);

  const cardTokenizeResponseReceived = (token, buyer) => {
    setLoading(true);
    // §card-on-file: relay the SCA buyer-verification token (buyer.token) so the
    // BE can CreateCard (store card on file) for the one-time med charge + the
    // membership subscription + the refill-triggered medication subscription;
    // name feeds CreateCustomer. Only present when verifyBuyer ran (gated below).
    const verificationToken = buyer && buyer.token ? buyer.token : undefined;
    const patientName = answers?.['basic-contact']?.name || undefined;
    // §membership-only path requires email — fall back to the contact answer.
    const patientEmail = email || answers?.['basic-contact']?.email || undefined;
    // §otp: phone-verify token captured on the contact slide; explicit prop wins.
    const effPhoneVerifyToken = phoneVerifyToken || answers?.['basic-contact']?.phoneVerifyToken || undefined;
    apiService
      .createSquarePayment(token, planId, answers, primaryGoal, (parseInt(currentStep) - 1), timestamp, patientEmail, selectedMedIds, prescriptionFulfillment, verificationToken, patientName, effPhoneVerifyToken, coupon?.code, { quoteId, visitType, pharmacySelection, otoCardOnFile: otoCardOnFile === true, dexaAddOn: dexaAddOn || undefined })
      .then((res) => {
        setLoading(false);
        // Persist paymentId so /beluga/create-visit-v2 (and autosave/resume) can read it.
        const paymentId = res?.paymentId || res?.payment_id;
        if (paymentId) {
          try { localStorage.setItem('fitpal_intake_payment_id', paymentId); } catch (e) {}
        }
        // §auth0-account: stash the one-time password-setup link (always write,
        // incl. '' to clear a stale link from a prior payment).
        try { localStorage.setItem('fitpal_auth0_setup_url', res?.auth0PasswordSetupUrl || ''); } catch (e) {}
        onComplete(res);
        console.info('Payment Success', res);
      })
      .catch((err) => {
        setLoading(false);
        console.error('Payment Error', err);
        const errData = err?.response?.data;
        // §branded-refill: the quote went stale between quote and pay (config
        // changed / expired). Nothing was charged. Hand it to the parent, which
        // re-quotes and re-renders the amount for the patient to confirm again.
        if (isQuoteStaleError(err) && typeof onQuoteStale === 'function') {
          onQuoteStale(errData || {});
          return;
        }
        // §coupons: the BE re-validates the coupon at capture and rejects a
        // no-longer-valid code with 400 {code, message} (coupon_invalid /
        // coupon_used) — nothing was charged. Remove the code so the total resets
        // and surface the reason; the patient can retry or pay without it.
        const couponErr = errData?.code || errData?.errorCode;
        if (couponErr === 'coupon_invalid' || couponErr === 'coupon_used') {
          setCoupon(null);
          alert(errData?.message || 'That coupon is no longer valid, so it was removed. Please review your total and try again — nothing was charged.');
          return;
        }
        // BE #349: DEXA add-on rejected before any charge.
        const dexaCode = String(errData?.code || errData?.errorCode || '').toLowerCase();
        if (dexaCode === 'dexa_price_changed') {
          const c = Number(errData?.cents);
          alert(`The DEXA scan price has changed${Number.isFinite(c) ? ` to ${fmtUsd(c)}` : ''}. Nothing was charged — please go back to the DEXA step, review the price and try again.`);
          return;
        }
        if (dexaCode === 'dexa_invalid') {
          alert('That DEXA option is no longer available. Nothing was charged — please go back to the DEXA step and choose 1 scan or the 3-scan bundle.');
          return;
        }
        // §card-on-file: card-save / subscription-enroll failures (402) get a
        // specific retry message — nothing was charged.
        const beCode = errData?.errorCode;
        if (beCode === 'MEMBERSHIP_ENROLL_FAILED' || beCode === 'CARD_SAVE_FAILED') {
          alert("We couldn't set up your membership — please check your card details and try again. Nothing was charged.");
          return;
        }
        // Map Square decline codes to a patient-friendly, actionable message.
        alert(describeSquareFailure(err));
      });
  };

  // §card-on-file SCA — GATED with CARD_ON_FILE_ENABLED (membership OR refill-
  // triggered subscriptions) so live tokenization is untouched until either
  // lights up (env flip + joint smoke). When on, verifyBuyer runs after tokenize
  // so `buyer.token` is a real Square verification token — REQUIRED by the BE to
  // CreateCard (card on file powers the one-time charge + the membership
  // subscription AND the refill-triggered medication subscription, see
  // BACKEND_SQUARE_SUBSCRIPTIONS_BILLING_PRD). intent CHARGE when money moves
  // today, STORE when today's total is $0 (Square disallows a 0-amount CHARGE).
  const verificationProps = cardOnFile
    ? {
        createVerificationDetails: () => {
          const fullName = answers?.['basic-contact']?.name || '';
          const parts = fullName.trim().split(/\s+/).filter(Boolean);
          // §coupons: verify against the DISCOUNTED total — the same number the
          // BE captures. Using the pre-discount `amount` made the SCA token
          // attest to a larger charge than the one presented, which issuers
          // reject (the patient sees a decline on a good card). Identical to
          // the old behavior with no coupon applied, since effectiveCents
          // falls back to baseCents. A 100%-off coupon lands on intent STORE,
          // which is also correct — Square disallows a 0-amount CHARGE.
          const cents = effectiveCents;
          const billingContact = {};
          if (parts[0]) billingContact.givenName = parts[0];
          if (parts.length > 1) billingContact.familyName = parts.slice(1).join(' ');
          const contactEmail = email || answers?.['basic-contact']?.email;
          if (contactEmail) billingContact.email = contactEmail;
          const details = { billingContact, intent: cents > 0 ? 'CHARGE' : 'STORE' };
          if (cents > 0) {
            details.amount = (cents / 100).toFixed(2);
            details.currencyCode = 'USD';
          }
          return details;
        },
      }
    : {};

  // §extra-payment-methods: Apple/Google Pay + Afterpay need a payment request
  // (amount + label) to render their sheets. Uses the discounted total when a
  // coupon is applied. Card + Cash App don't require it. Only built when enabled.
  const walletProps = EXTRA_PAY_METHODS_ENABLED
    ? {
        createPaymentRequest: () => ({
          countryCode: 'US',
          currencyCode: 'USD',
          total: { amount: (effectiveCents / 100).toFixed(2), label: 'Total' },
        }),
      }
    : {};

  return (
    <div>
      {COUPONS_ENABLED && (
        <div className="mb-4">
          <CouponField
            planId={planId}
            selectedMedIds={selectedMedIds}
            primaryGoal={primaryGoal}
            applied={coupon}
            onApplied={setCoupon}
            onCleared={() => setCoupon(null)}
          />
        </div>
      )}
      {/* §coupons: once a code is applied, show the recalculated total the patient
          will actually be charged BEFORE they pay (the parent step's summary still
          shows the pre-discount price — the BE charges the discounted total). Only
          rendered with an applied coupon + a known base amount, so no-coupon
          checkouts are visually unchanged. */}
      {coupon && baseCents > 0 && (
        <div className="mb-4 rounded-xl border border-gray-200 p-3 text-sm" data-testid="coupon-total-summary">
          <div className="flex items-center justify-between text-gray-500">
            <span>Subtotal</span>
            <span>{fmtUsd(baseCents)}</span>
          </div>
          <div className="flex items-center justify-between text-[#2d8a5f] mt-1">
            <span>Discount{coupon.code ? ` (${coupon.code})` : ''}</span>
            <span>−{fmtUsd(Number.isFinite(coupon.discountCents) ? coupon.discountCents : baseCents - effectiveCents)}</span>
          </div>
          <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-between font-semibold text-gray-900">
            <span>Total due today</span>
            <span data-testid="coupon-total-amount">{fmtUsd(effectiveCents)}</span>
          </div>
        </div>
      )}
      {EXTRA_PAY_METHODS_ENABLED && (
        <p className="text-sm font-semibold text-gray-900 mb-2">Choose a payment method</p>
      )}
      {/* Remount when the effective (discounted) amount changes so the wallet
          sheets re-initialize with the correct total. Constant when coupons +
          wallets are both off → identical to the prior card-only behavior. */}
      <PaymentForm
        key={effectiveCents}
        applicationId={sq.applicationId}
        cardTokenizeResponseReceived={cardTokenizeResponseReceived}
        {...verificationProps}
        {...walletProps}
        locationId={sq.locationId}
      >
        <CreditCard />
        {EXTRA_PAY_METHODS_ENABLED && (
          <>
            <Divider>or pay with</Divider>
            {/* 12px between wallet buttons (owner screenshot 2026-10-11: they sat
                flush). A flex gap, not margins: Apple Pay hides itself with
                display:none until it is available, and a hidden flex child takes no
                gap, so no empty space is left where it would be. */}
            <div className="flex flex-col gap-3" data-testid="wallet-buttons">
            {/* Apple Pay renders only on Apple devices (Safari / iOS) with a card in
                Wallet, and only once the domain is verified: the association file is
                served from public/.well-known/ and the domain must be registered with
                Square (Developer Console, or the RegisterDomain API). */}
            <ApplePay />
            <GooglePay />
            {/* Square renders Cash App Pay as a short pill by default; full width +
                semiround matches the Google Pay / Afterpay rectangles above and below
                (owner screenshot 2026-10-11). Square offers only round | semiround. */}
            <CashAppPay shape="semiround" width="full" />
            <Afterpay />
            </div>
          </>
        )}
      </PaymentForm>
    </div>
  );
};

export default SquarePaymentForm;
